<template>
  <div>
    <div class="row">
      <div class="form-group col-sm-12 col-lg-12 text-left">
        <label class="control-label">{{ $t("stock_management.contracts_purchase_sale.contract_type") }}</label>
        <DxRadioGroup
          v-model="localData.contract_type_id"
          :data-source="contractTypesList"
          :class="{ 'field-error': this.$v.localData.contract_type_id.$error }"
          display-expr="name"
          value-expr="id"
          layout="horizontal"
          @value-changed="oncontractTypeChanged"
          :disabled="isEditMode"
          ref="contractType"
        />
      </div>
    </div>
    <div v-if="isSaleContract" class="row">
      <div class="form-group col-sm-12 col-lg-12 text-left">
        <label class="control-label">{{ $t("stock_management.contracts_purchase_sale.sale_type") }}</label>
        <DxRadioGroup
          v-model="localData.sale_type"
          :data-source="saleTypeOptions"
          :class="{ 'field-error': this.$v.localData.sale_type?.$error }"
          display-expr="name"
          value-expr="id"
          layout="horizontal"
          @value-changed="onSaleTypeChanged"
          :disabled="isEditMode"
        />
      </div>
    </div>
    <div class="row">
      <div class="form-group col-sm-12 col-lg-4 text-left">
        <label class="control-label">{{ $t("common.legal_entity_name") }}</label>
        <DxSelectBox
          v-model="localData.legal_entity_uuid"
          :data-source="getLegalEntityList()"
          display-expr="name"
          value-expr="uuid"
          :on-value-changed="onLegalEntityChanged"
          :class="{
            'field-error': this.$v.localData.legal_entity_uuid?.$error,
          }"
          placeholder=""
        />
      </div>
      <div class="col-sm-4 col-lg-4 form-group">
        <label class="control-label">{{ $t("stock_management.contracts_purchase_sale.date") }}</label>
        <date-picker2
          v-model="localData.date"
          :value-type="'format'"
          :format="getDefaultDateFormat"
          :placeholder="getDefaultDateFormat"
          type="date"
          :input-class="['date-picker-style']"
          :class="{ 'field-error': this.$v.localData.date.$error }"
          @change="onDateChanged"
          ref="datePicker"
        ></date-picker2>
      </div>
    </div>
    <div class="row">
      <div class="form-group col-sm-12 col-lg-4 text-left">
        <label class="control-label">{{ $t("stock_management.contracts_purchase_sale.contract_number") }}</label>
        <DxAutocomplete
          v-model="localData.contract_number"
          :data-source="[]"
          :min-search-length="0"
          :show-clear-button="true"
          :class="{
            'field-error': this.$v.localData.contract_number?.$error,
          }"
        />
      </div>
      <div class="col-sm-4 col-lg-4 form-group">
        <template v-if="showPartnerSelector">
          <label class="control-label">{{ $t("common.partner") }}</label>
          <div
            class="sm-select-input-dialog-btn"
            @click="partnerClick"
            :class="{
              'field-error': this.$v.localData.partner_uuid?.$error,
            }"
          >
            <div class="sm-select-input-dialog-info">
              <div class="sm-select-input-dialog-value">{{ getPartnerNames }}</div>
            </div>
            <div class="sm-select-input-dialog-icon">
              <i class="fa-regular fa-edit"></i>
            </div>
          </div>
        </template>
        <template v-else>
          <label class="control-label">{{ $t("stock_management.contracts_purchase_sale.intragroup") }}</label>
          <DxSelectBox
            v-model="localData.intragroup_legal_entity_uuid"
            :data-source="getIntragroupLegalEntityList()"
            display-expr="name"
            value-expr="uuid"
            :on-value-changed="onIntragroupLegalEntityChanged"
            :class="{
              'field-error': this.$v.localData.intragroup_legal_entity_uuid?.$error,
            }"
            placeholder=""
          />
        </template>
      </div>
    </div>
    <div class="row">
      <div class="form-group col-sm-12 col-lg-4 text-left"></div>
      <div class="col-sm-4 col-lg-4 form-group">
        <label class="control-label">{{ $t("common.delivery_date") }}</label>
        <date-picker2
          v-model="localData.delivery_date"
          :value-type="'format'"
          :format="getDefaultDateFormat"
          :placeholder="getDefaultDateFormat"
          type="date"
          :input-class="['date-picker-style']"
          @change="onDeliveryDateChanged"
          ref="datePickerDelivery"
        ></date-picker2>
      </div>
    </div>
    <div v-if="isSaleContract" class="row">
      <div class="form-group col-sm-12 col-lg-4 text-left">
        <label class="control-label">{{ $t("stock_management.contracts_purchase_sale.document_supplier") }}</label>
        <div
          class="sm-select-input-dialog-btn"
          @click="documentSupplierClick"
        >
          <div class="sm-select-input-dialog-info">
            <div class="sm-select-input-dialog-value">{{ getDocumentSupplierName }}</div>
          </div>
          <div class="sm-select-input-dialog-icon">
            <i class="fa-regular fa-edit"></i>
          </div>
        </div>
      </div>
      <div class="col-sm-4 col-lg-4 form-group">
        <label class="control-label">{{ $t("stock_management.contracts_purchase_sale.document_client") }}</label>
        <div
          class="sm-select-input-dialog-btn"
          @click="documentClientClick"
        >
          <div class="sm-select-input-dialog-info">
            <div class="sm-select-input-dialog-value">{{ getDocumentClientName }}</div>
          </div>
          <div class="sm-select-input-dialog-icon">
            <i class="fa-regular fa-edit"></i>
          </div>
        </div>
      </div>
    </div>    
    <PartnerSelector
      ref="partnerSelector"
      :visible="showPartnerDialog"
      @close="showPartnerDialog = false"
      @continue="onContinueFromPartnerSelector"
      :multiselect="false"
    />
    <PartnerSelector
      ref="documentSupplierSelector"
      :visible="showDocumentSupplierDialog"
      @close="showDocumentSupplierDialog = false"
      @continue="onContinueFromDocumentSupplierSelector"
      :multiselect="false"
    />
    <PartnerSelector
      ref="documentClientSelector"
      :visible="showDocumentClientDialog"
      @close="showDocumentClientDialog = false"
      @continue="onContinueFromDocumentClientSelector"
      :multiselect="false"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { DxSelectBox } from "devextreme-vue/select-box";
import { DxRadioGroup } from "devextreme-vue/radio-group";
import DatePicker2 from "vue2-datepicker";
import required from "vuelidate/src/validators/required";
import requiredIf from "vuelidate/src/validators/requiredIf";
import moment from "moment";
import DxAutocomplete from "devextreme-vue/autocomplete";
import PartnerSelector from "@/components/PartnerSelector.vue";
import { CONTRACT_TYPES } from "@/pages/stock-management/config/contractTypes";

export default {
  name: "settings",
  props: {
    formData: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      localData: {
        ...this.formData,
        document_supplier_uuid: this.formData.document_supplier_uuid || null,
        document_supplier_name: this.formData.document_supplier_name || null,
        document_client_uuid: this.formData.document_client_uuid || null,
        document_client_name: this.formData.document_client_name || null,
      },
      contractTypesList: [],
      showPartnerDialog: false,
      showDocumentSupplierDialog: false,
      showDocumentClientDialog: false,
    };
  },
  computed: {
    ...mapGetters({
      getDefaultDateFormat: "settings/getDefaultDateFormat",
      departments: "company/departments",
    }),
    getPartnerNames() {
      if (!this.localData.partner_name) {
        return " - ";
      }
      return this.localData.partner_name;
    },
    getDocumentSupplierName() {
      if (!this.localData.document_supplier_name) {
        return " - ";
      }
      return this.localData.document_supplier_name;
    },
    getDocumentClientName() {
      if (!this.localData.document_client_name) {
        return " - ";
      }
      return this.localData.document_client_name;
    },
    isEditMode() {
      return !!this.localData.uuid;
    },
    isSaleContract() {
      return parseInt(this.localData.contract_type_id) === CONTRACT_TYPES.SALE;
    },
    saleTypeOptions() {
      return [
        { id: "partner", name: this.$t("common.partner") },
        { id: "intragroup", name: this.$t("stock_management.contracts_purchase_sale.intragroup") },
      ];
    },
    showPartnerSelector() {
      if (!this.isSaleContract) {
        return true;
      }
      return this.localData.sale_type !== "intragroup";
    },
  },
  components: {
    DxSelectBox,
    DxRadioGroup,
    DatePicker2,
    DxAutocomplete,
    PartnerSelector,
  },
  methods: {
    ...mapActions({
      getSmContractsPurchaseSaleTypes: "smContractsPurchaseSale/getSmContractsPurchaseSaleTypes",
      getDepartments: "company/getDepartments",
    }),
    syncContractTypeName() {
      if (this.localData.contract_type_id && this.contractTypesList.length > 0) {
        const match = this.contractTypesList.find((type) => parseInt(type.id) === parseInt(this.localData.contract_type_id));
        this.localData.contract_type = match?.name || this.localData.contract_type || "";
      }
    },
    ensureSaleTypeDefaults() {
      if (!this.isSaleContract) {
        this.localData.sale_type = null;
        this.localData.intragroup_legal_entity_uuid = null;
        this.localData.intragroup_legal_entity_name = null;
        this.localData.document_supplier_uuid = null;
        this.localData.document_supplier_name = null;
        this.localData.document_client_uuid = null;
        this.localData.document_client_name = null;
        return;
      }

      if (!this.localData.sale_type) {
        if (this.localData.intragroup_legal_entity_uuid) {
          this.localData.sale_type = "intragroup";
        } else {
          this.localData.sale_type = "partner";
        }
      }
    },
    onDateChanged(date) {},
    onDeliveryDateChanged(date) {},
    onContinueFromPartnerSelector(selectedPartners) {
      this.localData.partner_uuid = selectedPartners && selectedPartners.length > 0 ? selectedPartners[0].uuid : null;
      this.localData.partner_name = selectedPartners && selectedPartners.length > 0 ? selectedPartners[0].name : null;
      this.showPartnerDialog = false;
    },
    partnerClick() {
      this.showPartnerDialog = true; // Show the PartnerSelector dialog
    },
    documentSupplierClick() {
      this.showDocumentSupplierDialog = true;
    },
    documentClientClick() {
      this.showDocumentClientDialog = true;
    },
    onContinueFromDocumentSupplierSelector(selectedPartners) {
      this.localData.document_supplier_uuid = selectedPartners && selectedPartners.length > 0 ? selectedPartners[0].uuid : null;
      this.localData.document_supplier_name = selectedPartners && selectedPartners.length > 0 ? selectedPartners[0].name : null;
      this.showDocumentSupplierDialog = false;
    },
    onContinueFromDocumentClientSelector(selectedPartners) {
      this.localData.document_client_uuid = selectedPartners && selectedPartners.length > 0 ? selectedPartners[0].uuid : null;
      this.localData.document_client_name = selectedPartners && selectedPartners.length > 0 ? selectedPartners[0].name : null;
      this.showDocumentClientDialog = false;
    },
    onLegalEntityChanged(e) {
      if (e.value && this.departments.length > 0) {
        const selectedEntity = this.departments.find((dept) => dept.legal_entity?.uuid === e.value);
        if (selectedEntity) {
          this.localData.legal_entity_name = selectedEntity.legal_entity.name;
        }
      }

      if (e && e.event) {
        this.localData.intragroup_legal_entity_uuid = null;
        this.localData.intragroup_legal_entity_name = null;
      }
    },
    onIntragroupLegalEntityChanged(e) {
      if (e.value) {
        const selectedEntity = this.departments.find((dept) => dept.legal_entity?.uuid === e.value);
        this.localData.intragroup_legal_entity_name = selectedEntity?.legal_entity?.name || null;
      } else {
        this.localData.intragroup_legal_entity_name = null;
      }
    },
    oncontractTypeChanged(e) {
      if (e.value && this.contractTypesList.length > 0) {
        this.localData.contract_type = this.contractTypesList.find((type) => parseInt(type.id) === parseInt(e.value))?.name;
      }

      this.ensureSaleTypeDefaults();
    },
    onSaleTypeChanged(e) {
      if (e.value === "intragroup") {
        this.localData.partner_uuid = null;
        this.localData.partner_name = null;
      } else {
        this.localData.intragroup_legal_entity_uuid = null;
        this.localData.intragroup_legal_entity_name = null;
        this.localData.document_supplier_uuid = null;
        this.localData.document_supplier_name = null;
        this.localData.document_client_uuid = null;
        this.localData.document_client_name = null;
      }
    },
    getLegalEntityList() {
      return this.departments
        .filter((item) => item.is_legal_entity === 1 && item.legal_entity)
        .map((item) => ({
          uuid: item.legal_entity.uuid,
          name: item.legal_entity.name,
        }));
    },
    getIntragroupLegalEntityList() {
      return this.departments
        .filter((item) => item.is_legal_entity === 1 && item.legal_entity?.uuid !== this.localData.legal_entity_uuid)
        .map((item) => ({
          uuid: item.legal_entity.uuid,
          name: item.legal_entity.name,
        }));
    },
    validate() {
      this.$v.$touch(); // Trigger validation
      if (this.$v.$invalid) {
        return [this.$t("fill_out_error")];
      }
      return [];
    },
    updateData(newData) {
      if (JSON.stringify(this.localData) !== JSON.stringify(newData)) {
        this.localData = { ...newData }; // Update localData with new data

        this.syncContractTypeName();
        this.ensureSaleTypeDefaults();

        if (this.$refs.contractType) {
          this.$refs.contractType.instance.option("value", this.localData.contract_type_id);
        }
      }
    },
  },
  mounted() {
    // Set default date format if not already set
    if (!this.localData.uuid && !this.localData.date) {
      this.localData.date = moment().format(this.getDefaultDateFormat);
    }

    this.getDepartments({ limit: 1000 });

    this.getSmContractsPurchaseSaleTypes().then((response) => {
      if (response && response.length > 0) {
        this.contractTypesList = response.map((item) => ({
          id: parseInt(item.id),
          name: item.name,
          disabled: false,
        }));

        // Set default value for contract_type_id if not already set
        if (!this.localData.contract_type_id) {
          this.localData.contract_type_id = this.contractTypesList[0]?.id; // Default to the first item
          this.localData.contract_type = this.contractTypesList[0]?.name; // Set the name as well
        } else {
          this.syncContractTypeName();
        }

        this.ensureSaleTypeDefaults();
      }
    });
  },
  watch: {
    formData: {
      deep: true,
      handler(newData) {
        if (JSON.stringify(this.localData) !== JSON.stringify(newData)) {
          this.localData = { ...newData };
        }
      },
    },
    localData: {
      deep: true,
      handler(newData) {
        this.$emit("input", newData);
        this.$emit("update-form-data", newData, 1); // Emit updated data to parent
      },
    },
  },
  validations() {
    return {
      localData: {
        contract_type_id: {
          required,
        },
        date: {
          required,
        },
        sale_type: {
          required: requiredIf(() => this.isSaleContract),
        },
        partner_uuid: {
          required: requiredIf(() => this.showPartnerSelector),
        },
        intragroup_legal_entity_uuid: {
          required: requiredIf(() => this.isSaleContract && this.localData.sale_type === "intragroup"),
        },
        legal_entity_uuid: {
          required,
        },
        contract_number: {
          required,
        },
      },
    };
  },
};
</script>
<style scoped></style>
